import { Buffer } from "buffer";
import { isAfter } from "date-fns";
import log from "loglevel";
import { v4 } from "uuid";

import { TokenExpiredError } from "@/shared/errors";

import { pubSub, TOPIC_TOKEN_EXPIRED } from "../usePubSub";
import { TokenPayload } from "./types";

export const ACCESS_TOKEN_STORAGE_KEY = "accessToken";
const PI2_AUTH_TOKEN_STORAGE_KEY = "pi2.auth.token";
const X_CORRELATION_ID_KEY = "xCorrelationId";

export const xCorrelationIdProvider = {
    xCorrelationId: sessionStorage.getItem(X_CORRELATION_ID_KEY) || null,
    initXCorrelationId(): void {
        const newXCorrelationId = v4();
        sessionStorage.setItem(X_CORRELATION_ID_KEY, newXCorrelationId);
        this.xCorrelationId = newXCorrelationId;
    },
    getXCorrelationId(): string {
        if (this.xCorrelationId == null) {
            this.initXCorrelationId();
        }
        return this.xCorrelationId;
    },
    resetXCorrelationId(): void {
        sessionStorage.removeItem(X_CORRELATION_ID_KEY);
        this.xCorrelationId = null;
        this.initXCorrelationId();
    },
};

export const parseToken = (token: string): TokenPayload => {
    if (!token) return undefined;
    try {
        return JSON.parse(Buffer.from(token.split(".")[1], "base64").toString("utf-8"));
    } catch (e) {
        log.error(e);
        return null;
    }
};

export const tokenProvider = {
    token: sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || "",
    getToken(): string {
        if (!this.token) return;

        const parsedToken = this.getParsedToken();
        const expirationDate = new Date(0);
        expirationDate.setUTCSeconds(parsedToken.exp);

        const todaysDate = new Date();

        if (isAfter(todaysDate, expirationDate)) {
            const error = new TokenExpiredError();
            pubSub.publish(TOPIC_TOKEN_EXPIRED, { error });
            throw error;
        }
        return this.token;
    },
    getParsedToken(): TokenPayload {
        if (!this.token) return;
        const parsedToken = parseToken(this.token);
        return parsedToken;
    },
    setToken(token: string): void {
        sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
        this.token = token;
    },
    clearToken(): void {
        sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
        this.token = "";
    },
};

export const pi2AuthTokenProvider = {
    token: sessionStorage.getItem(PI2_AUTH_TOKEN_STORAGE_KEY) || null,
    getToken(): string {
        return this.token;
    },
    setToken(newToken: string): void {
        sessionStorage.setItem(PI2_AUTH_TOKEN_STORAGE_KEY, newToken);
        this.token = newToken;
    },
    clearToken(): void {
        sessionStorage.removeItem(PI2_AUTH_TOKEN_STORAGE_KEY);
        this.token = null;
    },
};
